import { Controller } from "stimulus";

const DARK_TEXT = "#1d1d2b";
const LIGHT_TEXT = "#ffffff";
const FALLBACK_NAME = "Charcoal";
const HEX_REGEXP = /^#?[0-9a-fA-F]{6}$/;

const rgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const luminance = (hex) => {
  const [r, g, b] = rgb(hex).map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a, b) => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

const textFor = (hex) =>
  contrast(hex, LIGHT_TEXT) >= contrast(hex, DARK_TEXT) ? LIGHT_TEXT : DARK_TEXT;

const saturationAndLightness = (hex) => {
  const [r, g, b] = rgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const s =
    max === min
      ? 0
      : l > 0.5
      ? (max - min) / (2 - max - min)
      : (max - min) / (max + min);
  return { s, l };
};

const hueOf = (hex) => {
  const [r, g, b] = rgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === min) return -1;
  const d = max - min;
  const h =
    max === r
      ? (g - b) / d + (g < b ? 6 : 0)
      : max === g
      ? (b - r) / d + 2
      : (r - g) / d + 4;
  return h * 60;
};

const normalize = (value) => {
  const v = (value || "").trim();
  if (!HEX_REGEXP.test(v)) return null;
  return (v[0] === "#" ? v : `#${v}`).toUpperCase();
};

export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "editButton",
    "suggested",
    "swatches",
    "name",
    "customButton",
    "customPanel",
    "nativeColor",
    "nativeSwatch",
    "hexField",
    "result",
  ];

  static values = {
    palette: Array,
  };

  connect() {
    this.color =
      normalize(this.inputTarget.value) || this.paletteValue[0].hex.toUpperCase();
    this.isCustom = !this.findSwatch(this.color);
    this.editing = false;
    this.panelOpen = false;
    this.startHex = null;
    this.buildSwatches();
    this.render();
    if (this.inputTarget.value.toUpperCase() !== this.color) {
      this.inputTarget.value = this.color;
    }
  }

  findSwatch(hex) {
    return this.paletteValue.find((p) => p.hex.toUpperCase() === hex);
  }

  buildSwatches() {
    this.swatchesTarget.querySelectorAll("[data-swatch]").forEach((el) => el.remove());
    const before = this.customButtonTarget;
    this.paletteValue.forEach((p) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "scp-swatch";
      button.dataset.swatch = p.hex.toUpperCase();
      button.dataset.action = "click->suggested-color-picker#pick keydown->suggested-color-picker#keydown";
      button.setAttribute("role", "radio");
      button.setAttribute("aria-label", p.name);
      button.title = p.name;
      button.style.background = p.hex;
      button.style.color = textFor(p.hex);
      button.innerHTML = '<i class="fa-solid fa-check"></i>';
      this.swatchesTarget.insertBefore(button, before);
    });
  }

  toggleEdit() {
    this.editing = !this.editing;
    this.panelOpen = this.editing && this.isCustom;
    this.render();
  }

  doneCustom() {
    this.panelOpen = false;
    if (this.color !== this.startHex) this.editing = false;
    this.render();
  }

  closeEditor() {
    this.editing = false;
    this.panelOpen = false;
  }

  pick(event) {
    this.isCustom = false;
    this.closeEditor();
    this.setColor(event.currentTarget.dataset.swatch);
  }

  openCustom() {
    this.isCustom = true;
    this.panelOpen = true;
    this.startHex = this.color;
    this.render();
    this.hexFieldTarget.focus();
  }

  keydown(event) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!keys[event.key]) return;
    event.preventDefault();
    const buttons = this.radioButtons();
    const index = buttons.indexOf(event.currentTarget);
    const next = buttons[(index + keys[event.key] + buttons.length) % buttons.length];
    next.focus();
    next.click();
  }

  radioButtons() {
    return Array.from(this.swatchesTarget.querySelectorAll('[role="radio"]'));
  }

  nativeInput(event) {
    event.stopPropagation();
    const hex = normalize(event.target.value);
    if (hex) this.setColor(hex, { notify: event.type === "change" });
  }

  nativeChange(event) {
    event.stopPropagation();
    const hex = normalize(event.target.value);
    if (hex) this.setColor(hex);
  }

  hexChange(event) {
    event.stopPropagation();
    const hex = normalize(event.target.value);
    if (hex) {
      this.setColor(hex);
    } else {
      this.hexFieldTarget.value = this.color;
    }
  }

  hexInput(event) {
    event.stopPropagation();
    const hex = normalize(event.target.value);
    if (hex && hex !== this.color) this.setColor(hex);
  }

  hexKeyup(event) {
    event.stopPropagation();
  }

  useNearest() {
    this.isCustom = false;
    this.closeEditor();
    this.setColor(this.nearest(this.color).hex.toUpperCase());
  }

  setColor(hex, { notify = true } = {}) {
    this.color = hex;
    this.render();
    if (this.inputTarget.value.toUpperCase() !== hex) {
      this.inputTarget.value = hex;
      if (notify) {
        this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  nearest(hex) {
    const hue = hueOf(hex);
    const fallback = this.paletteValue.find((p) => p.name === FALLBACK_NAME);
    if (hue < 0) return fallback || this.paletteValue[0];
    let best = this.paletteValue[0];
    let bestDistance = Infinity;
    this.paletteValue.forEach((p) => {
      const ph = hueOf(p.hex);
      if (ph < 0) return;
      const diff = Math.abs(ph - hue);
      const distance = Math.min(diff, 360 - diff);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = p;
      }
    });
    return best;
  }

  check(hex) {
    const lum = luminance(hex);
    const { s, l } = saturationAndLightness(hex);
    const text = textFor(hex);
    const ratio = contrast(hex, text);
    if (lum >= 0.8) {
      return {
        title: "Too light for the calendar",
        message: "Very light colours blend into the calendar background, so the booking is easy to miss.",
      };
    }
    if (s > 0.85 && l > 0.45 && lum > 0.4) {
      return {
        title: "Too bright next to other bookings",
        message: "Neon colours are hard on the eyes and overpower the other services on the calendar.",
      };
    }
    if (ratio < 4.5) {
      return {
        title: "Text will be hard to read",
        message: `Booking text only reaches ${ratio.toFixed(1)}:1 contrast on this colour (4.5:1 is the minimum for readable text).`,
      };
    }
    return null;
  }

  render() {
    const hex = this.color;
    const fg = textFor(hex);
    const swatch = this.findSwatch(hex);
    const isCustom = this.isCustom;

    this.previewTarget.style.background = hex;
    this.previewTarget.style.color = fg;
    this.previewTarget.style.setProperty("--scp-color", hex);

    this.nameTarget.textContent = isCustom ? `Custom · ${hex}` : swatch.name;

    this.radioButtons().forEach((button) => {
      const selected = !isCustom && button.dataset.swatch === hex;
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-checked", selected ? "true" : "false");
      button.tabIndex = selected || (isCustom && button === this.customButtonTarget) ? 0 : -1;
      if (button.dataset.swatch) {
        button.style.boxShadow = selected ? `0 0 0 2px #fff, 0 0 0 4px ${button.dataset.swatch}` : "";
      }
    });

    const inner = this.customButtonTarget.querySelector(".scp-custom-inner");
    const icon = inner.querySelector("i");
    this.customButtonTarget.classList.toggle("is-selected", isCustom);
    inner.style.background = isCustom ? hex : "#fff";
    icon.className = isCustom ? "fa-solid fa-check" : "fa-solid fa-plus";
    icon.style.color = isCustom ? fg : "";

    const showPanel = this.editing && isCustom && this.panelOpen;
    this.suggestedTarget.hidden = !this.editing;
    this.customPanelTarget.hidden = !showPanel;
    this.editButtonTarget.classList.toggle("is-editing", this.editing);
    this.editButtonTarget.setAttribute("aria-expanded", this.editing ? "true" : "false");
    this.editButtonTarget.querySelector("i").className = this.editing ? "fa-solid fa-xmark" : "fa-solid fa-palette";
    this.editButtonTarget.querySelector("span").textContent = this.editing ? "Close" : "Change";
    this.nativeSwatchTarget.style.background = hex;
    this.nativeColorTarget.value = hex.toLowerCase();
    if (document.activeElement !== this.hexFieldTarget) {
      this.hexFieldTarget.value = hex;
    }

    if (showPanel) this.renderResult(hex, fg);
  }

  renderResult(hex, fg) {
    const problem = this.check(hex);
    this.resultTarget.innerHTML = "";
    if (!problem) {
      const ok = document.createElement("span");
      ok.className = "scp-ok";
      const icon = document.createElement("i");
      icon.className = "fa-solid fa-circle-check";
      const text = document.createElement("span");
      text.textContent = `Easy to read. Text will be ${fg === LIGHT_TEXT ? "white" : "dark"}.`;
      ok.append(icon, text);
      this.resultTarget.append(ok);
      return;
    }

    const near = this.nearest(hex);
    const warn = document.createElement("div");
    warn.className = "scp-warn";
    warn.innerHTML = `
      <i class="fa-solid fa-triangle-exclamation"></i>
      <div class="scp-warn-body">
        <span><b class="scp-warn-title"></b> <span class="scp-warn-message"></span></span>
        <button type="button" class="scp-fix" data-action="suggested-color-picker#useNearest">
          <span class="scp-fix-dot"></span><span class="scp-fix-label"></span>
        </button>
      </div>`;
    warn.querySelector(".scp-warn-title").textContent = `${problem.title}.`;
    warn.querySelector(".scp-warn-message").textContent = problem.message;
    warn.querySelector(".scp-fix-dot").style.background = near.hex;
    warn.querySelector(".scp-fix-label").textContent = `Use ${near.name} instead`;
    this.resultTarget.append(warn);
  }
}
